import * as _ from "underscore"

import { Enums } from "../../models/common"
import { EventsNames, ToastHelper, LocalizationHelper, Dictionary, LogHelper, ConvertHelper } from "../../helpers"
import { USER_SESSION_SERVICE_NAME, UserSessionService } from "../user-session"
import { $ROOT_SCOPE_SERVICE_NAME, OpenDetailsOrModuleOptions, SYSTEM_SERVICE_NAME, SystemService } from "../system"
import { IReferenceType } from "../../models/util/interfaces"
import { LOCATION_SERVICE_NAME, LocationService } from "../location"
import { OpLocation } from "../../models/core"
import { Source } from "../../modules/common/select-box-popup/select-box-popup.directive"

//#region Public Types

export const SYSTEM_CONTEXT_LIST_SERVICE_NAME = 'system.contextListService'
export const SYSTEM_CONTEXT_LIST_SERVICE_PROVIDER_NAME = 'system.contextListServiceProvider'

export type RedirectOptions = {
    redirectToSelect: {
        id: number,
        name: string
    }[],
    referenceObjects: Dictionary<IReferenceType[]>
}

//#endregion

export class SystemContextListService {

    private readonly LOG_PREFIX = '[SIMAX.Services.ContextList]'

    private get popupId(): string { return 'destinationSelectPopup' }
    private referenceObjects: Dictionary<IReferenceType[]>

    // #region Ctor

    constructor(
        private $rootScope: ng.IRootScopeService,
        private userSession: UserSessionService,
        private systemService: SystemService,
        private locationService: LocationService
    ) { }

    // #endregion


    // #region Public Methods

    public getContextList(destinationType: number, selectedIds: number[], referenceType: Enums.ReferenceType): void {
        this.$rootScope.$broadcast('moduleChanging')

        this.userSession
            .getDataProvider()
            .System
            .getContextList(referenceType, destinationType, selectedIds)
            .then((response: IReferenceType[]) => {
                if (_.size(response) > 0) {
                    const result: number[] = response.map((referenceObject: IReferenceType) => {
                        return referenceObject.getReferenceKey()
                    })

                    const openDetailsOrModuleOptions: OpenDetailsOrModuleOptions = {
                        idReferenceType: destinationType,
                        referenceObjectIds: result
                    }

                    if (destinationType === Enums.ReferenceType.IdLocation) {
                        this.redirectToLocations(response as OpLocation[])
                    } else {
                        this.systemService.openDetailsOrModule(openDetailsOrModuleOptions)
                    }
                } else {
                    this.showNoItemsToShowToast()
                }
            })
            .fail((error: any) => {
                LogHelper.warn(this.LOG_PREFIX, `Unable to load context list for destinationType: ${destinationType}`, error)
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
            })
            .always(() => {
                this.$rootScope.$broadcast('stopModuleChanging')
            })
    }

    public getPopupOptions(): Source {
        return {
            popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectModuleToRedirect),
            submitButtonText: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Submit),
            itemsList: null,
            selectedValue: null,
            onSubmit: (value: number) => { this.redirectTo(value) },
            onHiding: () => { this.$rootScope.$broadcast('stopModuleChanging') }
        }
    }

    public getDestinationPopupId(): string {
        return this.popupId
    }

    // #endregion


    // #region Private Methods    

    private redirectToLocations(locations: OpLocation[]): void {
        this.referenceObjects = this.locationService.getContextListReferenceObjects(locations)
        const redirectToSelect: { id: number, name: string }[] = this.locationService.getWhereToRedirect(this.referenceObjects)

        if (this.referenceObjects.length === 0) {
            this.showNoItemsToShowToast()
            this.$rootScope.$broadcast('stopModuleChanging')
        } else if (this.referenceObjects.length === 1) {
            this.redirectTo(this.referenceObjects.keys()[0] as number)
        } else {
            this.$rootScope.$broadcast(EventsNames.OPEN_SELECT_BOX_POPUP + this.popupId, redirectToSelect, redirectToSelect[0].id)
        }
    }

    private redirectTo(moduleName: Enums.SimaxModuleName): void {
        if (moduleName == null) {
            this.$rootScope.$broadcast('stopModuleChanging')
            return
        }

        const referenceObjectIds: number[] = this.referenceObjects.tryGetValue(moduleName).map((reference: IReferenceType) => reference.getReferenceKey())

        if (_.size(referenceObjectIds) > 0) {
            const openDetailsOrModuleOptions: OpenDetailsOrModuleOptions = {
                idReferenceType: null,
                moduleName: moduleName,
                referenceObjectIds: referenceObjectIds
            }
            this.systemService.openDetailsOrModule(openDetailsOrModuleOptions)
        } else {
            this.showNoItemsToShowToast()
        }
        this.$rootScope.$broadcast('stopModuleChanging')
    }

    private showNoItemsToShowToast(): void {
        ToastHelper.showInfo(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoItemsToShow))
    }

    // #endregion
}

export class SystemContextListServiceProvider
    implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        $ROOT_SCOPE_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        LOCATION_SERVICE_NAME,
        function (
            $rootScope: ng.IRootScopeService,
            userSession: UserSessionService,
            systemService: SystemService,
            locationService: LocationService
        ) {
            return new SystemContextListService($rootScope, userSession, systemService, locationService)
        }]

    //#endregion
} 
